import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Text,
  HStack,
  Flex,
  Button,
  IconButton,
  Tooltip,
  ButtonProps,
} from "@chakra-ui/react";
import {
  getAccountInitials,
  getAccountShortName,
} from "@syla/shared/types/models/AccountAccessBase";
import { AccountAccessDetails } from "@syla/shared/types/responses/GetAccountsResponse";
import React, { forwardRef } from "react";
import { SkeletonRect } from "../../atoms/Skeletons";

type CurrentAccountButtonProps = {
  currentAccount: AccountAccessDetails | undefined;
  isCollapsed?: boolean;
} & ButtonProps;

export const CurrentAccountButton = forwardRef<
  HTMLButtonElement,
  CurrentAccountButtonProps
>(function CurrentAccountButton(
  { currentAccount, isCollapsed, onClick, ...props },
  ref,
) {
  const accountName =
    currentAccount ? getAccountShortName(currentAccount) : "...";

  if (isCollapsed) {
    return (
      <Tooltip label={accountName} placement="right" hasArrow>
        <IconButton
          ref={ref}
          aria-label="Switch account"
          variant="outline"
          colorScheme="gray"
          backgroundColor="gray.200"
          role="group"
          p={0}
          onClick={onClick}
          icon={<AccountInitialsBox currentAccount={currentAccount} />}
          {...props}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      ref={ref}
      variant="outline"
      colorScheme="gray"
      w="full"
      h="auto"
      p={2}
      justifyContent="flex-start"
      textAlign="left"
      role="group"
      whiteSpace="normal"
      onClick={onClick}
      {...props}
    >
      <SkeletonRect isLoaded={!!currentAccount} w="full">
        <HStack w="full" minW={0}>
          <AccountInitialsBox currentAccount={currentAccount} />
          <DisplayName account={currentAccount} />
          <ChevronDownIcon flexShrink={0} color="gray.100" boxSize="1.5rem" />
        </HStack>
      </SkeletonRect>
    </Button>
  );
});

function AccountInitialsBox({
  currentAccount,
}: {
  currentAccount: AccountAccessDetails | undefined;
}) {
  return (
    <Flex
      flexShrink={0}
      boxSize="2.25rem"
      rounded="md"
      align="center"
      justify="center"
      backgroundColor="gray.200"
      _groupHover={{ bg: "gray.100" }}
    >
      <Text fontSize="xs" fontWeight="bold" color="gray.700">
        {currentAccount ? getAccountInitials(currentAccount) : ".."}
      </Text>
    </Flex>
  );
}

function displayName(account?: AccountAccessDetails): {
  title: string;
  subtitle?: string;
} {
  if (!account) {
    return {
      title: "..",
      subtitle: undefined,
    };
  }

  if (account.fullName) {
    return {
      title: account.fullName,
      subtitle: account.nickname,
    };
  }

  return {
    title: account.nickname ?? "..",
    subtitle: undefined,
  };
}

function DisplayName({ account }: { account?: AccountAccessDetails }) {
  const { title, subtitle } = displayName(account);

  return (
    <Text
      flex={1}
      minW={0}
      fontSize="xs"
      color="gray.100"
      noOfLines={2}
      wordBreak="break-word"
    >
      <Text as="span" fontWeight="medium">
        {title}
      </Text>

      {subtitle && (
        <Text as="span" fontWeight="light">
          {" "}
          ({subtitle})
        </Text>
      )}
    </Text>
  );
}
